import { Button } from "@/components/ui/button";
import { useState, type ReactNode } from "react";
import { ImportantNoteModal } from "./ImportantNoteModal";
import { Progress } from "@/components/ui/progress";
import { Footer } from "@/components/layout/Footer";
import { CircleAlert, FileText } from "lucide-react";

interface StepLayoutProps {
  currentStep: number;
  totalSteps?: number;
  showImportantNote?: boolean;
  showReference?: boolean;
  selectedRegion?: string;
  children: ReactNode;
}

export const StepLayout = ({
  currentStep,
  totalSteps = 4,
  showImportantNote = false,
  showReference = true,
  selectedRegion = "",
  children,
}: StepLayoutProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Calculate progress width based on current step
  const progressWidth = (currentStep / totalSteps) * 100; // 192px = w-48
  return (
    <div
      className={`relative flex h-screen flex-col overflow-y-auto ${
        currentStep === 4 ? "" : "bg-gray-50"
      } `}
    >
      {/* Header with Logo and Buttons */}
      <header className="border-b border-gray-200 bg-white shadow-sm">
        <div className="container mx-auto flex items-center justify-between px-8 py-4">
          <img
            src="/images/logos/ultomiris-logo.svg"
            alt="ULTOMIRIS Logo"
            className="h-16 w-20 sm:w-44"
          />
          <div className="flex items-center space-x-4">
            {showImportantNote && (
              <Button
                variant="outline"
                className="rounded-3xl border-none bg-gray-100 text-base font-medium text-secondary hover:bg-secondary/10 hover:text-secondary"
                onClick={() => setIsModalOpen(true)}
              >
                <CircleAlert className="h-7 w-5 sm:me-2" />
                <span className="hidden sm:block">Important Note</span>
              </Button>
            )}
            {showReference && (
              <Button
                variant="outline"
                className="rounded-3xl border-none bg-gray-100 text-base font-medium text-primary hover:bg-primary/10 hover:text-primary"
              >
                <FileText className="h-7 w-5 sm:me-2" />
                <span className="hidden sm:block">Reference</span>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Progress Indicator */}

      {/* Main Content */}
      <main className="container mx-auto flex-1 px-8">
        {currentStep !== 4 && (
          <div className="flex items-center justify-center px-8 py-8">
            <Progress
              value={progressWidth}
              className="h-2 w-[100%] sm:w-[50%]"
            />
          </div>
        )}
        {children}
      </main>

      {/* Footer */}
      <Footer />

      {/* Important Note Modal */}
      <ImportantNoteModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedRegion={selectedRegion}
      />
    </div>
  );
};
